import type { MillsheetSortConfig, SortKeySpec } from '@mbfab/shared'
import { isSome, sortMillsheetItems, sortMillsheets } from '@mbfab/shared'

import type { ColumnConfig } from '../../order-matching/print/svg/main-row'
import type { MillsheetRow } from '../types'

import { defaultPrintSortDirection, REORDERABLE_COLUMN_TYPES, resolvePrintSortDirection } from './home-print-columns'

//
// ホーム（ミルシート一覧）の印刷・データ出力で、原本の印刷順・ページ番号・Excel/CSV の行順を決める並び替え。
// Excel 用の表示項目のうち「並び替えできる項目」の左右順がそのまま優先順位、項目ごとの昇順／降順が方向になる
// （v1 MillsheetPrintForm の sortKeys / itemSortKeys の復活。一覧画面の並び順は使わない）。
// 原本・Excel・CSV が同じ配列から作られないと順序がずれるため、ダイアログの入口で1回だけ呼ぶ。
//

// 明細の属性で、ミルシート内の明細の並びにも使う項目
// 工区・節・部位は PR #390 で明細の属性になり、ミルシート（親）の値は「A工区, B工区」のような集約文字列のため、規格・形状と同じく明細から取る
const ITEM_LEVEL_SORT_KEYS: Array<ColumnConfig['type']> = [
  'steelType',
  'sectionType',
  'constructionArea',
  'member',
  'buildingPart',
]

export function resolvePrintSortKeys(excelColumnConfigs: readonly ColumnConfig[]): SortKeySpec[] {
  const keys = excelColumnConfigs
    .filter((config) => config.active && REORDERABLE_COLUMN_TYPES.includes(config.type))
    .map((config): SortKeySpec => ({ key: config.type, direction: resolvePrintSortDirection(config) }))
  // 並び替え項目がすべて OFF のときも順序が揺れないよう、証明書番号（既定の方向）にする
  return keys.length > 0 ? keys : [{ key: 'no', direction: defaultPrintSortDirection('no') }]
}

export function sortMillsheetsForPrint(
  millsheets: readonly MillsheetRow[],
  sortConfig: MillsheetSortConfig,
  excelColumnConfigs: readonly ColumnConfig[],
): MillsheetRow[] {
  const sortKeys = resolvePrintSortKeys(excelColumnConfigs)
  const itemSortKeys = sortKeys.filter((x) => ITEM_LEVEL_SORT_KEYS.some((type) => type === x.key))

  const prepared = millsheets.map((millsheet) => {
    const sortedItems = sortMillsheetItems(
      millsheet.millsheetItems.map((item, index) => ({
        index,
        steelType: item.steelType,
        sectionType: item.sectionType,
        constructionArea: item.constructionArea,
        member: item.member,
        buildingPart: item.buildingPart,
      })),
      sortConfig,
      itemSortKeys,
    ).flatMap((x) => {
      const item = millsheet.millsheetItems[x.index]
      return isSome(item) ? [item] : []
    })
    const first = sortedItems[0]
    // 並び替えキーは親の値を上書きせず別オブジェクトに持つ（出力する行データの親の値を変えないため）
    return {
      millsheet: { ...millsheet, millsheetItems: sortedItems },
      manufacturerName: millsheet.manufacturerName,
      no: millsheet.no,
      orderNumber: millsheet.orderNumber,
      // 明細にしか無い属性は、明細内の並び替え後の先頭の値をミルシートの並び替えキーに使う
      steelType: first?.steelType ?? '',
      sectionType: first?.sectionType ?? '',
      constructionArea: first?.constructionArea ?? '',
      member: first?.member ?? '',
      buildingPart: first?.buildingPart ?? '',
    }
  })
  return sortMillsheets(prepared, sortConfig, sortKeys).map((x) => x.millsheet)
}
